import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { api, fmtDateTime, fmtINR } from "@/lib/api";
import { maskMobile } from "@/lib/membership";
import { Search, PlusCircle, Receipt, CreditCard } from "lucide-react";
import MembershipCardModal from "@/components/MembershipCardModal";

export default function AgentSales() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cardOf, setCardOf] = useState(null);

  useEffect(() => {
    api.get("/agent/me/memberships?limit=200").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (m) =>
        m.patient_name?.toLowerCase().includes(term) ||
        m.patient_mobile?.includes(term) ||
        m.membership_number?.toLowerCase().includes(term)
    );
  }, [rows, q]);

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-1.5">
            My sales
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            All cards you’ve sold
          </h1>
        </div>
        <Link to="/agent/new">
          <Button className="bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant">
            <PlusCircle className="h-4 w-4" /> Sell new card
          </Button>
        </Link>
      </div>

      <Card className="border-border/70 shadow-soft">
        <CardContent className="p-5 sm:p-6">
          <div className="relative max-w-md mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, mobile or membership number"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-10 pl-9"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Receipt className="h-8 w-8 mx-auto text-primary/60 mb-2" />
              No sales found.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left font-semibold px-5 sm:px-6 py-3">Patient</th>
                    <th className="text-left font-semibold py-3">Membership #</th>
                    <th className="text-left font-semibold py-3">Sold on</th>
                    <th className="text-right font-semibold py-3">Sale</th>
                    <th className="text-right font-semibold py-3">Your earn</th>
                    <th className="text-right font-semibold px-5 sm:px-6 py-3">Card</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/40">
                      <td className="px-5 sm:px-6 py-3">
                        <div className="font-medium text-foreground">{m.patient_name}</div>
                        <div className="text-xs text-muted-foreground">{maskMobile(m.patient_mobile)}</div>
                      </td>
                      <td className="py-3 font-mono-card text-xs tracking-wider text-foreground">
                        {m.membership_number}
                      </td>
                      <td className="py-3 text-muted-foreground">{fmtDateTime(m.issued_at)}</td>
                      <td className="py-3 text-right">{fmtINR(m.amount)}</td>
                      <td className="py-3 text-right">
                        <div className="font-semibold text-foreground">+{fmtINR(m.commission_amount)}</div>
                        <Badge
                          className={
                            "mt-1 text-[10px] " +
                            (m.commission_paid
                              ? "bg-success/12 text-success border border-success/25 hover:bg-success/12"
                              : "bg-warning/15 text-warning-foreground border border-warning/30 hover:bg-warning/15")
                          }
                        >
                          {m.commission_paid ? "Paid" : "Pending"}
                        </Badge>
                      </td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <Button variant="outline" size="sm" className="h-8" onClick={() => setCardOf(m)}>
                          <CreditCard className="h-3.5 w-3.5" /> View card
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <MembershipCardModal
        open={!!cardOf}
        onOpenChange={() => setCardOf(null)}
        membership={cardOf}
      />
    </DashboardLayout>
  );
}
